import { Cancellation, useAuthFetch } from "@p0security/frontend-core";
import { toAssessmentPath } from "@p0security/shared/assessment/helper";
import { AggregatedNode } from "@p0security/shared/graph/aggregate";
import {
  ALL_SCOPE_SENTINEL,
  toKey,
  toScope,
} from "@p0security/shared/types/assessment";
import {
  AnyAggregates,
  AnyNode,
  AssessmentNodes,
} from "@p0security/shared/types/assessment/data/index";
import { Finding } from "@p0security/shared/types/assessment/finding";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { CollectExportData, Export } from "components/Export";
import { Tenant } from "components/Login";
import { ResizableTableColumnType } from "components/ResizableTable";
import { getDocs } from "firebase/firestore";
import { noop } from "lodash";
import { useCallback, useContext, useMemo } from "react";

import { AllIntegrationLogos } from "../../Integrations/IntegrationLogo.tsx";
import { FindingsContext, findingsQuery } from "../contexts/FindingsContext";
import { NodeWithScope, isExportColumn, toJson, toTsv } from "../utils/export";
import { aggregateGraphForShow, fetchGraphForScope } from "../utils/graph";
import { monitorFindingsToNodes } from "../utils/monitor";

export const DetailedFindingsExport = (props: {
  selected: AggregatedNode<
    AssessmentNodes,
    keyof AssessmentNodes,
    AnyAggregates
  >[];
  selectedScope: string;
  columns: ResizableTableColumnType<AnyNode>[];
}) => {
  const { columns, selected, selectedScope } = props;

  const { assessment, last } = useContext(SelectedEnvironmentContext);
  const tenantId = useContext(Tenant);

  const authFetch = useAuthFetch();
  const { selectedMonitor: monitor } = useContext(FindingsContext);
  const exportable = useMemo(() => columns.filter(isExportColumn), [columns]);

  const toJsonAll = useMemo(() => toJson(exportable, true), [exportable]);
  const toTsvAll = useMemo(() => toTsv(exportable, true), [exportable]);
  const toJsonScope = useMemo(() => toJson(exportable, false), [exportable]);
  const toTsvScope = useMemo(() => toTsv(exportable, false), [exportable]);
  const collect: CollectExportData<NodeWithScope> = useCallback(
    async (onProgress, cancellation) => {
      const graphCancellation = new Cancellation();

      const output: NodeWithScope[] = [];
      if (!assessment.doc || !last.doc || !monitor) return output;
      const { show, countsByScope } = monitor;
      const scopes = last.doc?.data.scope ?? [];

      const assessmentId = assessment.doc.id;
      let index = 0;

      for (const scope of scopes) {
        if (cancellation?.current || graphCancellation.isCancelled)
          return output;
        onProgress(`(${index} / ${scopes.length})`, index / scopes.length);
        index++;
        const scopeKey = toKey(scope);
        // If there are no findings for this scope, we don't need to fetch the graph or query firestore
        if (!countsByScope[scopeKey]) continue;

        const scopeGraph = await fetchGraphForScope({
          assessmentId,
          authFetch,
          cancellation: graphCancellation,
          effectiveScopeKey: scopeKey,
          lastJobId: last.doc.id,
          setGraph: noop,
          setMetaGraph: noop,
          setPercentage: noop,
          setStep: noop,
        });

        if (!scopeGraph) continue;
        const aggregated = await aggregateGraphForShow({
          cancellation: graphCancellation,
          graph: scopeGraph,
          setAggregated: noop,
          show,
        });
        if (!aggregated) continue;

        const findings: Finding[] = (
          await getDocs(
            findingsQuery({
              assessmentPath: toAssessmentPath(tenantId, assessment.doc.id),
              monitorId: monitor.monitorId,
              scopeKey: toKey(scope),
              state: "open",
              trigger: "all",
              assignment: "all",
              lastJob: last.doc?.data.jobId ?? "",
              start: undefined,
              end: undefined,
            })
          )
        ).docs.map((d) => d.data() as Finding);
        const nodes = monitorFindingsToNodes(findings, aggregated).nodes.map(
          (n) => ({ node: n, scope: scopeKey })
        );
        output.push(...nodes);
      }

      return output;
    },
    [assessment.doc, last.doc, authFetch, monitor, tenantId]
  );

  const selectedNodesWithScope = selected.map((node) => ({
    node,
    scope: selectedScope,
  }));

  const scope = toScope(selectedScope);
  if (scope === ALL_SCOPE_SENTINEL) return null;

  const formattedScope = (format: "JSON" | "TSV") => (
    <>
      Export&nbsp;
      {AllIntegrationLogos[scope.integration]}
      &nbsp;
      {scope.id}&nbsp;as&nbsp;
      {format}
    </>
  );

  const formattedScopeAll = (format: "JSON" | "TSV") => (
    <>
      Export all findings as&nbsp;
      {format}
    </>
  );

  const filenameAll = `${assessment.doc?.data.name}_${monitor?.monitorId}_all-scopes`;
  const filenameScope = `${assessment.doc?.data.name}_${monitor?.monitorId}_${selectedScope}`;

  return (
    <Export
      data={collect}
      filename={filenameScope}
      options={{
        json: {
          label: formattedScope("JSON"),
          blob: toJsonScope,
          extension: "json",
          optionData: selectedNodesWithScope,
        },
        tsv: {
          label: formattedScope("TSV"),
          blob: toTsvScope,
          extension: "tsv",
          optionData: selectedNodesWithScope,
        },
        jsonAll: {
          label: formattedScopeAll("JSON"),
          blob: toJsonAll,
          extension: "json",
          filenameVariant: filenameAll,
        },
        tsvAll: {
          label: formattedScopeAll("TSV"),
          blob: toTsvAll,
          extension: "tsv",
          filenameVariant: filenameAll,
        },
      }}
      help="export only the selected scope"
      title="Export"
    />
  );
};
